import { Box, Grid, Stack, Typography, useTheme } from "@mui/material";
import React from "react";
import EventIcon from "@mui/icons-material/Event";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { GenericRechartsTooltip } from "../../components/CustomMUI.js";

export default function ReleaseYear({ data, isOwnProfile }) {
  const theme = useTheme();
  const { release_year_counts } = data;
  const currentYear = new Date().getFullYear();
  const minYear = Math.min(...release_year_counts.map((d) => d.release_year));

  // Process release year data to give zero counts for all years between first release year and today
  const denseReleaseYearData = Array.from(
    { length: currentYear - minYear + 1 },
    (_, i) => {
      const year = minYear + i;
      const existing = release_year_counts.find((d) => d.release_year === year);

      return {
        release_year: year,
        watch_count: existing ? existing.watch_count : 0,
      };
    }
  );

  const decadeTicks = denseReleaseYearData
    .map((d) => d.release_year)
    .filter((year) => year % 10 === 0);

  const isEmptyHistogram = data.release_year_counts.length === 0;

  return (
    <Grid
      item
      xs={12}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        width: "100%",
        mt: 2,
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        <EventIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: "bold", lineHeight: 1 }}>
          {isOwnProfile ? "Your watches" : "Watches"} by release year...
        </Typography>
      </Stack>
      <Box
        sx={{
          width: "100%",
          height: isEmptyHistogram ? 150 : { xs: 200, sm: 250 },
          position: "relative",
          mt: 1,
        }}
      >
        <ResponsiveContainer>
          <BarChart
            data={denseReleaseYearData}
            margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
          >
            <XAxis
              dataKey="release_year"
              tick={{ fontSize: 12 }}
              ticks={decadeTicks}
            />

            <YAxis hide />

            <Tooltip
              cursor={{ fill: theme.palette.action.hover }}
              content={
                <GenericRechartsTooltip
                  renderContent={(payload) => {
                    const count = payload.watch_count;
                    const noun = count === 1 ? "movie" : "movies";

                    return `${count} ${noun} released in ${payload.release_year}`;
                  }}
                />
              }
            />

            <Bar
              dataKey="watch_count"
              fill={theme.palette.tertiary.main}
              radius={[
                theme.shape.borderRadius,
                theme.shape.borderRadius,
                0,
                0,
              ]}
            />
          </BarChart>
        </ResponsiveContainer>
        {isEmptyHistogram && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: 12,
                color: "text.secondary",
                textAlign: "center",
                fontStyle: "italic",
                transform: "translateY(-12px)",
              }}
            >
              No watch data to show
            </Typography>
          </Box>
        )}
      </Box>
    </Grid>
  );
}
